/**
 * 我的分析页面 表格组件
 */
// 1. 外部库
import React, {useEffect, useImperativeHandle, useMemo, useState} from 'react';
import {history} from 'umi';
import {useRequest} from 'ahooks';
import {Button, ConfigProvider, notification, PagingQuery, Table, TableColumn, Text} from 'tea-component';

// 2. 内部工具和配置
import { useUrlParam } from "@dbl/core/global";
import {SYSTEM_NAME} from '@dbl/core/global';
import {getLocalText, translateSpecialWord, useDblIntl} from '@/pages/datalab/utils/utils';
import { COMPONENT_CONFIG } from '@dbl/core/chartEditor';
import {SORT_COLUMNS} from '../constants';
import {DEFAULT_PAGE_SIZE} from '@dbl/core/global';

// 3. 类型定义
import type {IChartTableParams, IChartTableProps, IChartTableRef, IGetChartListSubLabelRequest} from '../types';

// 4. 服务
import { getChartList } from '@dbl/services/chart';
import { shareChart } from '../service';

// 5. 组件
import DeleteModal from './DeleteModal';
import CopyChartModal from './CopyChartModal';
import {useGetChartCreatorData} from './hoooks';
import AccessSettingModal, {Editor} from "@/pages/datalab/components/AccessSettingModal";
import BubbleEllipsis from "@/pages/datalab/components/BubbleEllipsis";
import TextBubbles from '@/pages/home/components/TextBubbles';
import {TableHoverFiledSearchBox} from '../../Home/RecentlyAndShare';

const {pageable, scrollable, sortable, autotip} = Table.addons;

/**
 * 更新用户列宽度配置
 * 根据语言环境设置不同的列宽度，英文环境需要更宽的列以容纳较长的用户名
 */
const UPDATE_USER_COLUMN_WIDTH = {
  en: 175,
  zh: 160,
} as const;

/** 是否展示复制按钮：后端 copy 优先；未下发时需同时具备 read 与 edit */
const canShowCopyButton = (record: {
  copy?: boolean;
  read?: boolean;
  edit?: boolean;
}): boolean => record?.copy ?? (Boolean(record?.read) && Boolean(record?.edit));

/** 统计单行可见操作按钮数，用于动态计算操作列宽 */
const getChartRowActionCount = (record: {
  access?: boolean;
  delete?: boolean;
  copy?: boolean;
  read?: boolean;
  edit?: boolean;
}): number => {
  let count = 0;
  if (record.access) {
    count += 1;
  }
  if (canShowCopyButton(record)) {
    count += 1;
  }
  if (record.delete) {
    count += 1;
  }
  return count;
};

/** 操作列宽度随当前页最多按钮数变化，避免无复制权限时空列过宽 */
const getOperationColumnWidth = (maxActionCount: number, isEnLocale: boolean): number => {
  if (maxActionCount <= 1) {
    return isEnLocale ? 100 : 90;
  }
  if (maxActionCount === 2) {
    return isEnLocale ? 150 : 130;
  }
  return isEnLocale ? 200 : 170;
};

const ChartTable = React.forwardRef<IChartTableRef, IChartTableProps>((props, ref) => {
  const { searchWords, gameCode } = props;
  const [sort, setSort] = useUrlParam({
    urlKey: 'sort',
    defaultValue: {
      by: 'create_time',
      order: 'desc',
    },
  });

  // 分页信息
  const [pageInfo, setPageInfo] = useUrlParam({
    urlKey: 'pageInfo',
    defaultValue: {
      pageSize: DEFAULT_PAGE_SIZE,
      pageIndex: 1,
    },
  });
  // 表格记录类型定义
  interface IChartTableRecord {
    task_id: number;
    name: string;
    name_cn?: string;
    label?: string;
    chart_type: string;
    chart_dataset_list?: Array<{
      customize_name_cn?: string;
      customize_name_en?: string;
      table_name?: string;
    }>;
    create_time?: string;
    owner?: string;
    update_user_name?: string;
    editable_person_name_list?: string[];
    editable_person_list?: Array<{
      user_id: string;
      user_name: string;
      permission: string[];
    }>;
    user_id?: string;
    access?: boolean;
    delete?: boolean;
    edit?: boolean;
    read?: boolean;
    copy?: boolean;
    use_report_name?: string[];
    type?: number;
  }

  // useRequest 返回类型定义
  interface IChartListResponse {
    row_count: number;
    data_list: IChartTableRecord[];
  }

  interface IUseRequestReturn<T> {
    data: T | undefined;
    loading: boolean;
    error: Error | undefined;
    run: (params?: IRequestChartTableParams) => Promise<T>;
    refresh: () => void;
  }

  // 请求参数类型定义
  interface IRequestChartTableParams {
    pageInfo: {
      pageSize: number;
      pageIndex: number;
    };
    sortInfo: {
      by: string;
      order: 'asc' | 'desc';
    };
    quickResetValue: {
      chartTypes: string[];
      creators: string[];
      updaters: string[];
      dataSource: string[];
    };
    sub_labels?: IGetChartListSubLabelRequest[];
    all_selected?: boolean;
    keywords?: string;
  }

  const [deleteRecord, setDeleteRecord] = useState<IChartTableRecord | undefined>();
  const [copyRecord, setCopyRecord] = useState<IChartTableRecord | undefined>();
  const [canDeleted, setCanDeleted] = useState<boolean>(true);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [accessInfo, setAccessInfo] = useState<{id: number; editors: Editor[]; creatorId: string;}>({id: -1, editors: [], creatorId: ''}); // 权限弹窗需要的信息
  const [quickValue, setQuickValue] =
    useUrlParam({
      urlKey: 'quickValue',
      defaultValue: {
        chartTypes: [],
        creators: [],
        updaters: [],
        dataSource: [],
      },
    });
  const {dblFormatMessage, commonFormatMessage, isEn} = useDblIntl('myAnalysis');

  /**
   *
   * @param pageInfo 分页信息, 主要用于在切换分页条数, 跳转页码时, 传入最新分页信息
   * @param sortInfo 排序信息, 主要用于在点按排序按钮时, 传入最新排序信息
   * @return 返回 请求异步方法
   */
  const requestChartTable = ({pageInfo, sortInfo, quickResetValue, ...rest}: IRequestChartTableParams): Promise<IChartListResponse> => {
    const params: IChartTableParams = {
      game_code: props?.gameCode,
      sub_labels: props?.subLabels,
      all_selected: props?.allSelected,
      keywords: searchWords,
      order_by: sortInfo?.by,
      desc: sortInfo?.order === 'desc',
      page: pageInfo?.pageIndex,
      page_size: pageInfo?.pageSize,
      system: 'datalab',
      filter_template: true,
      creators: quickResetValue?.creators,
      update_users: quickResetValue?.updaters,
      chart_types: quickResetValue?.chartTypes, 
      data_source: quickResetValue?.dataSource,
      ...rest
    };
    return getChartList(params);
  };

  const { data: creatorData } = useGetChartCreatorData(
    useMemo(
      () => ({
        game_code: gameCode,
        all_selected: true,
        keywords: searchWords,
        system: 'datalab',
        filter_template: true,
      }),
      [gameCode, searchWords],
    ),
  );
  // 使用useRequest 可以减少loading和error的额外设置代码, 且在类目快速点击切换时, 可以竞态取消，当上一次请求还没返回时，又发起了下一次请求，则会取消上一次请求
  const {data, loading, error, run, refresh}: IUseRequestReturn<IChartListResponse> = useRequest(
    requestChartTable,
    {
      manual: true,
      onError: (e) => {
        console.error('报表图表请求错误: ', e);
      },
    },
  );

  /** 发布或删除任务 */
  const {run: shareChartRun} = useRequest(
    (params) => shareChart(params),
    {
      manual: true,
      onSuccess: () => {
        // 删除成功通知
        notification.success({
          description: commonFormatMessage('delete_successfully'),
          duration: 1000,
        });
        props.requestChartLabels();
        // 如果删除的是当前页最后一条数据，则重置到第一页
        // 添加边界情况处理：确保 total 和 pageInfo 有效
        const isReset = pageInfo?.pageIndex > 1 && total > 0 && (total - 1 === (pageInfo?.pageIndex - 1) * pageInfo?.pageSize);
        if (isReset) {
          setPageInfo({ pageSize: pageInfo?.pageSize, pageIndex: 1 });
        }
        run({
          pageInfo: isReset ? { ...pageInfo, pageIndex: 1 } : pageInfo,
          quickResetValue: quickValue,
          sortInfo: sort,
        });
      },
      onError: () => {
        // 删除失败通知
        notification.error({
          description: commonFormatMessage('delete_failed'),
          duration: 1000,
        });
      },
    },
  );

 
  const { chartTypes, creators, updaters, dataSource } = useMemo(() => {
    const { chart_types: chartTypes, creators, updaters, data_source: dataSource } = creatorData || {};
    const getValue = (data?: Array<string>, isSingle?: boolean) =>
      data?.map((item) => {
        const text = isSingle
          ? COMPONENT_CONFIG?.[item]?.[isEn ? 'name' : 'nameCn'] || item || '-'
          : item?.user_name || '-';
        return {
          value: isSingle ? item : item?.user_id,
          text,
          title: text,
        };
      });
    return {
      chartTypes: getValue(chartTypes, true),
      creators: getValue(creators),
      updaters: getValue(updaters),
      dataSource: getValue(dataSource, true),
    };
  }, [creatorData, isEn]);

  useEffect(() => {
    run({
      pageInfo,
      sortInfo: sort,
      quickResetValue: quickValue,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 只在组件挂载时执行一次，明确注释说明为什么依赖项为空
  
  useImperativeHandle(ref, () => ({
    searchChartList: (searchWords) =>
    {
      run({
        keywords: searchWords,
        pageInfo: { pageIndex: 1, pageSize: DEFAULT_PAGE_SIZE },
        sortInfo: {
          by: 'create_time',
          order: 'desc',
        },
        quickResetValue: {
          chartTypes: [],
          creators: [],
          updaters: [],
          dataSource: [],
        },
        sub_labels: [],
        all_selected: true,
      });
    },
    categoryFilterChartList: (category) =>
    {
      run({
        pageInfo,
        sortInfo: sort,
        quickResetValue: quickValue,
        sub_labels: category?.subLabels ?? [],
        all_selected: category?.allSelected,
      });
    },
    resetState: () => {
      setPageInfo({ pageIndex: 1, pageSize: DEFAULT_PAGE_SIZE });
      setQuickValue({
        chartTypes: [],
        creators: [],
        updaters: [],
        dataSource: [],
      });
      setSort({
        by: 'create_time',
        order: 'desc',
      });
    }
  }));

  const handleOnClickCreateSearch = <K extends keyof typeof quickValue>(
    key: K,
    value: typeof quickValue[K]
  ) => {
    setPageInfo((res)=>({...res, pageIndex: 1}));
    setQuickValue((res)=>({...res, [key]: value }));
    run({
      pageInfo: {pageSize: pageInfo.pageSize, pageIndex: 1},
      sortInfo: sort,
      quickResetValue: {
        ...quickValue, [key]: value
      },
    }
    );
  };
  // 删除报表
  const deletedFunc = () => {
    shareChartRun({
      game_code: props.gameCode,
      task_id: deleteRecord?.task_id,
      operation_type: 0,
    });
  };

  const jumpToDetail = (record: IChartTableRecord): void => {
    const query = {
      from: 'edit',
      taskId: record.task_id,
      name: record?.name,
      nameCn: record?.name_cn || '',
    };
    
    if (record.type === 1) {
      
      // 探索分析
      history.push({
        pathname: `/${SYSTEM_NAME}/${props?.gameCode}/Action/ExploreAnalysis`,
        query
      });
    }  if (record.type === 2) {
      // AB实验
      history.push({
        pathname: `/${SYSTEM_NAME}/${props?.gameCode}/Analysis/ABTest`,
        query
      });
    } else {
      // 多维分析
      history.push({
        pathname: `/${SYSTEM_NAME}/${props?.gameCode}/Analysis/MultiAnalysis`,
        query,
      });
    }
  };

  const operationColumnWidth = useMemo(() => {
    const list = data?.data_list || [];
    const maxActionCount = list.reduce(
      (max, record) => Math.max(max, getChartRowActionCount(record)),
      0,
    );
    return getOperationColumnWidth(maxActionCount, isEn);
  }, [data?.data_list, isEn]);

  const columns: TableColumn[] = useMemo(() => [
    {
      key: 'name',
      header: dblFormatMessage('chart_name'),
      width: 400,
      fixed: 'left',
      render(record: IChartTableRecord) {
        return (
          <TextBubbles
            className={'dbl-mydashboard-table-name'}
            text={(isEn ? record?.name : record?.name_cn || record?.name) || '-'}
            onClick={() => {
              if (!record.edit) {
                notification.warning({
                  description: commonFormatMessage('no_edit_access_tips'),
                });
              } else {
                jumpToDetail(record);
              }
            }}
          />
        );
      },
    },
    {
      key: 'label',
      header: dblFormatMessage('lable'),
      width: 100,
      render(record: IChartTableRecord) {
        return translateSpecialWord(record?.label, isEn) || '-';
      },
    },
    {
      key: 'chart_type',
      header: <TableHoverFiledSearchBox {
        ...{
          data: chartTypes,
          handleOnClickCreateSearch: (value)=>handleOnClickCreateSearch('chartTypes', value),
          value: quickValue?.chartTypes,
          buttonText: dblFormatMessage('chart_type'),
        }
      }/>,
      width: 140,
      render(record: IChartTableRecord) {
        return (
          COMPONENT_CONFIG?.[record?.chart_type]?.[
            isEn ? 'name' : 'nameCn'
          ] || record?.chart_type || '-'
        );
      },
    },
    {
      key: 'table_name_list',
      header: <TableHoverFiledSearchBox {
        ...{
          data: dataSource,
          handleOnClickCreateSearch: (value) => handleOnClickCreateSearch('dataSource', value),
          value: quickValue?.dataSource,
          buttonText: dblFormatMessage('chart_data_source'),
          listWidth: 340,
        }
      }/>,
      width: 230,
      render: (cvm: IChartTableRecord) => {
        return cvm?.chart_dataset_list?.map((task) => {
          return getLocalText(isEn, task.customize_name_cn, task.customize_name_en, task.table_name || '-');
        })?.join(', ') || '-';
      }
    },
    {
      key: 'create_time',
      header: dblFormatMessage('creation_time'),
      width: 180,
      render: (cvm: IChartTableRecord) => cvm?.create_time || '-'
    },
    {
      key: 'owner',
      header: <TableHoverFiledSearchBox {
        ...{
          data: creators,
          handleOnClickCreateSearch: (value)=>handleOnClickCreateSearch('creators', value),
          value: quickValue?.creators,
          buttonText: dblFormatMessage('chart_creator'),
        }
      }/>,
      width: 130,
      render: (cvm: IChartTableRecord) => cvm?.owner || '-'
    },
    {
      key: 'update_user_name',
      header: <TableHoverFiledSearchBox {
        ...{
          data: updaters,
          handleOnClickCreateSearch: (value)=>handleOnClickCreateSearch('updaters', value),
          value: quickValue?.updaters,
          buttonText: commonFormatMessage('last_updater'),
        }
      }/>,
      width: isEn ? UPDATE_USER_COLUMN_WIDTH.en : UPDATE_USER_COLUMN_WIDTH.zh,
      render: (cvm: IChartTableRecord) => cvm?.update_user_name || '-'
    },
    {
      key: 'editable_person',
      header: commonFormatMessage('editable_person'),
      width: 150,
      render: (cvm: IChartTableRecord) => {
        const names = cvm?.editable_person_name_list?.filter((name: string) => name !== cvm.owner)?.join(',');
        return (<BubbleEllipsis
          content={names || '-'}
        />);
      }
    },
    {
      key: 'handle',
      header: dblFormatMessage('operation'),
      width: operationColumnWidth,
      fixed: 'right',
      render: (record: IChartTableRecord) => (
        <div className="analyze-table-flex">
          {
            record.access ? (<Button
              className="table-btn"
              type="link"
              onClick={() => {
                const editors: Editor[] = [];
                record?.editable_person_list?.forEach((person) => {
                  if (person.user_id === record.user_id) return;
                  editors.push({
                    userName: person?.user_name,
                    userId: person?.user_id,
                    // 后端只返回一个 read权限, 或者有两个 read时, viewer是勾选状态
                    isView: person?.permission?.length === 1 ? person?.permission?.includes('read') : person?.permission?.filter(access => access === 'read')?.length === 2,
                    isDevelop: person?.permission?.length > 1,
                  });
                });
                setAccessInfo({
                  id: record.task_id,
                  editors,
                  creatorId: record.user_id
                });
              }
              }
            >
              {commonFormatMessage("access")}
            </Button>) : ''
          }
          {canShowCopyButton(record) && (
            <Button
              className="table-btn"
              type="link"
              onClick={() => {
                setCopyRecord(record);
              }}
            >
              {commonFormatMessage('copy')}
            </Button>
          )}
          {/* 和王婕确认后必须是自己创建的，或是三种管理员才有权限删除 */}
          {/* 产品要求developer可以删除, 增加了图表权限的功能, 以后端返回的为准 */}
          {record.delete && (
            <Button
              className="table-btn"
              type="link"
              onClick={() => {
                setDeleteRecord(record);
                // 如果图表已被引用，则给出已经被引用的提示，否则给出删除提示
                setCanDeleted(record?.use_report_name?.length === 0 || !record?.use_report_name);
                setShowDeleteModal(true);
              }}
            >
              {dblFormatMessage("delete")}
            </Button>
          )}
        </div>
      ),
    },
  ], [chartTypes, creators, dataSource, quickValue?.dataSource, quickValue?.chartTypes, quickValue?.creators, quickValue?.updaters, updaters, operationColumnWidth, dblFormatMessage, commonFormatMessage, isEn]);

  // 总条数
  const total: number = data?.row_count || 0;
  // 搜索是否有结果
  const isFound = !!props?.searchWords && total === 0;
  // 表格数据
  const records = loading || isFound || error ? [] : data?.data_list || []; // 当 loading, isFound , error状态下, tea表格默认显示之前的数据源, 但需求是不显示, 所以设置为空数组.

  return (
    <div>
      <ConfigProvider locale={isEn ? 'en' : 'zh'}>
        <Table
          recordKey="task_id"
          className="analyze-table"
          columns={columns}
          records={records}
          addons={[
            scrollable({
              minWidth: 1200,
            }),
            pageable({
              isPagingReset: true,
              pageSize: pageInfo.pageSize,
              pageIndex: pageInfo.pageIndex,
              pageSizeOptions: [10, 20, 50, 100],
              recordCount: total || 0,
              stateText: dblFormatMessage('total', {total}),
              stateTextVisible: total > 10,
              pageSizeVisible: total > 10,
              pageIndexVisible: total > 10,
              jumpVisible: total > 10,
              endJumpVisible: total > 10,
              onPagingChange: (query: PagingQuery) => {
                setPageInfo(query);
                // 请求表格数据
                run({pageInfo: query, sortInfo: sort, quickResetValue: quickValue});
              },
            }),
            sortable({
              columns: SORT_COLUMNS,
              value: [sort],
              onChange: (value: Array<{by: string; order: 'asc' | 'desc';}> | []) => {
                // 如果value数组没有元素说明当前是未排序状态, 那么上传给后端的参数就应该是按照创建时间降序排序
                const sortValue = value[0] ?? { by: 'create_time', order: 'desc' as const };
                setSort(sortValue);
                run({pageInfo, sortInfo: sortValue, quickResetValue: quickValue});
              },
            }),
            autotip({
              isLoading: loading,
              isError: !!error,
              isFound: Boolean(props?.searchWords) && total === 0, // 搜索是否有结果
              foundText: commonFormatMessage('not_search'),
              onClear: () => {
                // 搜索结果为空时, 显示返回原列表按钮, 同时设置按钮功能
                props.clearSearchWords();
              },
              style: {lineHeight: '200px'},
              errorText: (
                <span
                  className="tea-action-state__text tea-text-danger"
                  title={commonFormatMessage('load_fail')}
                >
                  {commonFormatMessage('load_fail')}
                  <Text
                    theme="primary"
                    onClick={() => {
                      window.location.reload(true);
                    }}
                    style={{cursor: 'pointer'}}
                  >
                    {commonFormatMessage('refresh')}
                  </Text>
                </span>
              ),
            }),
          ]}
        />
      </ConfigProvider>
      {showDeleteModal && (
        <DeleteModal
          setShowDeleteModal={setShowDeleteModal}
          deletedFunc={deletedFunc}
          deleteRecord={deleteRecord}
          canDeleted={canDeleted}
        />
      )}
      <CopyChartModal
        visible={!!copyRecord}
        gameCode={gameCode}
        record={copyRecord}
        onClose={() => {
          setCopyRecord(undefined);
        }}
        onSuccess={() => {
          props.requestChartLabels();
          refresh();
        }}
      />
      <AccessSettingModal
        visible={accessInfo.id !== -1}
        onClose={(isSuccess) => {
          setAccessInfo({id: -1, editors: [], creatorId: ''});
          if (isSuccess) {
            refresh();
          }
        }}
        id={accessInfo?.id}
        creatorId={accessInfo?.creatorId}
        editors={accessInfo?.editors}
        type="chart"
      />
    </div>
  );
});

ChartTable.displayName = 'ChartTable';

export default ChartTable;
